export const hasPreOrderCode = (preOrderCode) => (
  preOrderCode !== undefined
  && preOrderCode !== null
  && preOrderCode !== ''
);

export const shouldUsePreOrderPaymentPrintMode = ({
  explicitPreOrderPayment,
  preOrderInvoice,
  dataPreOrder,
} = {}) => (
  explicitPreOrderPayment === true
  || preOrderInvoice === true
  || dataPreOrder?.pre_Order === true
);

const getPrinterCategoryId = (category = {}) => (
  category.category_id ?? category.id ?? category.group_category_id
);

const getItemCategoryId = (item = {}) => (
  item.category_id ?? item.category?.category_id ?? item.category?.id
);

const isAssignedToKitchenPrinter = (item = {}, printers) => {
  if (!Array.isArray(printers)) return true;

  const kitchenPrinters = printers.filter((printer) => printer?.printer_type === "Comanda");
  if (kitchenPrinters.length === 0) return false;

  const itemCategoryId = String(getItemCategoryId(item) ?? "");
  if (!itemCategoryId) return true;

  const kitchenPrintersWithCategories = kitchenPrinters.filter((printer) => (
    Array.isArray(printer.categories) && printer.categories.length > 0
  ));

  if (kitchenPrintersWithCategories.length === 0) return true;

  return kitchenPrintersWithCategories.some((printer) => (
    printer.categories.some((category) => String(getPrinterCategoryId(category) ?? "") === itemCategoryId)
  ));
};

const shouldSendLineToKitchen = (item, options = {}) => {
  if (!item || item.kitchen_action !== "send_to_kitchen") return false;
  if (!isAssignedToKitchenPrinter(item, options.printers)) return false;

  const amountToImpress = Number(item.amount_to_impress || 0);

  return item.isNewAdd === true || amountToImpress > 0;
};

export const hasPendingKitchenItems = (groups = [], combos = [], options = {}) => (
  groups.some((item) => shouldSendLineToKitchen(item, options))
  || combos.some((item) => shouldSendLineToKitchen(item, options))
);

const shouldPrintPreOrderLineToKitchen = (item, { printUnmarkedItems = true } = {}) => {
  if (!item || item.kitchen_action !== "send_to_kitchen") return false;

  const amountToImpress = Number(item.amount_to_impress || 0);

  return item.isNewAdd === true
    || amountToImpress > 0
    || (printUnmarkedItems && item.is_printed !== true);
};

export const hasPreOrderKitchenItemsToPrint = (groups = [], combos = [], options = {}) => (
  groups.some((item) => shouldPrintPreOrderLineToKitchen(item, options))
  || combos.some((item) => shouldPrintPreOrderLineToKitchen(item, options))
);

export const normalizeRecoveredPreOrderItems = (items = []) => (
  items.map((item) => {
    if (!item) return item;

    const normalizedItem = {
      ...item,
      amount_Original: item.amount_Original ?? item.amount,
    };

    if (normalizedItem.kitchen_action !== "send_to_kitchen") {
      return normalizedItem;
    }

    return {
      ...normalizedItem,
      isNewAdd: false,
      amount_to_impress: 0,
    };
  })
);

export const preservePreOrderOperationalState = (item = {}, source = {}) => ({
  ...item,
  amount_Original: source.amount_Original ?? item.amount_Original ?? source.amount ?? item.amount,
  kitchen_action: source.kitchen_action ?? item.kitchen_action,
  is_printed: source.is_printed ?? item.is_printed,
  isNewAdd: source.isNewAdd ?? item.isNewAdd,
  amount_to_impress: source.amount_to_impress ?? item.amount_to_impress,
});

const getSegmentIdentity = (item = {}) => {
  const segments = item.segments || item.DetalleSegmentos || [];

  if (segments.length > 0) {
    return segments
      .map((segment) => `${segment.segment_id || ""}:${segment.item_id || segment.item?.item_id || ""}`)
      .sort()
      .join("|");
  }

  return item.segments_string || "";
};

const getPreOrderLineKey = (item = {}) => {
  const segmentIdentity = getSegmentIdentity(item);

  if (item.combo_id !== undefined && item.combo_id !== null) {
    return `combo:${item.combo_id}:${segmentIdentity}`;
  }

  if (item.group_id !== undefined && item.group_id !== null) {
    return `group:${item.group_id}:${segmentIdentity}`;
  }

  return null;
};

export const preserveKitchenStateFromSource = (items = [], sourceItems = []) => {
  const sourceByKey = new Map(
    sourceItems
      .map((item) => [getPreOrderLineKey(item), item])
      .filter(([key]) => Boolean(key))
  );

  return items.map((item) => {
    const source = sourceByKey.get(getPreOrderLineKey(item));
    if (!source) return item;

    return preservePreOrderOperationalState(item, source);
  });
};
